import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Loader2, CheckCircle2, PartyPopper } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";

const Field = ({ label, hint, children }) => (
  <label className="block text-left">
    <span className="mb-1.5 block text-sm font-medium text-white/80">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-white/40">{hint}</span>}
  </label>
);

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/30 outline-none transition-colors focus:border-indril-saffron/60";

export const FounderModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ full_name: "", mobile: "", email: "", indril_user_id: "", accept_terms: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const reset = () => { setDone(false); setError(""); setForm({ full_name: "", mobile: "", email: "", indril_user_id: "", accept_terms: false }); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.accept_terms) { setError("Please accept the Program Terms to continue."); return; }
    setLoading(true);
    try {
      await api.post("/founders/register", form);
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 300); }}>
      <DialogContent className="max-w-md border-white/10 bg-zinc-950/95 text-white backdrop-blur-xl" data-testid="founder-modal">
        <DialogTitle className="sr-only">Founder Creator Registration</DialogTitle>
        <DialogDescription className="sr-only">Apply to become one of the first 10,000 IND RIL Founder Creators.</DialogDescription>
        <AnimatePresence mode="wait">
          {!done ? (
            <motion.form key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onSubmit={submit} className="space-y-4">
              <div className="text-center">
                <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-indril-saffron/15 text-indril-saffron"><Gift className="h-6 w-6" /></span>
                <h3 className="font-display text-2xl font-semibold">Claim Your Founder Spot</h3>
                <p className="mt-1 text-sm text-white/50">Apply to become one of the first 10,000 Founder Creators.</p>
              </div>

              <Field label="Full Name">
                <input required className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Your full name" data-testid="founder-full-name" />
              </Field>
              <Field label="Mobile Number">
                <input required className={inputCls} value={form.mobile} onChange={set("mobile")} placeholder="10-digit mobile number" inputMode="numeric" data-testid="founder-mobile" />
              </Field>
              <Field label="Email Address">
                <input required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="founder-email" />
              </Field>
              <Field label="IND RIL User ID" hint="Enter your exact account User ID. Example: @username">
                <input required className={inputCls} value={form.indril_user_id} onChange={set("indril_user_id")} placeholder="@username" data-testid="founder-user-id" />
              </Field>

              <label className="flex items-start gap-3 text-left">
                <input type="checkbox" checked={form.accept_terms} onChange={set("accept_terms")} className="mt-1 h-4 w-4 accent-indril-saffron" data-testid="founder-terms" />
                <span className="text-xs text-white/60">
                  I accept the{" "}
                  <Link to="/legal/founder-terms" target="_blank" className="text-indril-saffron hover:underline">Founder Creator Program Terms</Link>. I understand submission does not guarantee approval or a badge.
                </span>
              </label>

              {error && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="founder-error">{error}</p>}

              <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-indril-saffron px-6 py-3.5 font-display text-lg font-semibold text-zinc-950 disabled:opacity-60" data-testid="founder-submit">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit Application"}
              </button>
            </motion.form>
          ) : (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="py-4 text-center" data-testid="founder-success">
              <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indril-green/15 text-indril-green"><PartyPopper className="h-7 w-7" /></span>
              <h3 className="font-display text-2xl font-semibold">Registration Submitted</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">
                Thank you for applying to the IND RIL Founder Creator Program. Your application is under review. Registration does not guarantee approval.
                Once the campaign reaches 10,000 verified and approved Founder Creators, eligible users will receive the Founder Creator Badge after final review.
              </p>
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-white/40"><CheckCircle2 className="h-3.5 w-3.5 text-indril-green" /> Please check your IND RIL account for future updates.</p>
              <div className="mt-6 flex flex-col gap-2 sm:flex-row">
                <button onClick={() => onOpenChange(false)} className="flex-1 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-zinc-950" data-testid="founder-continue">Continue to IND RIL</button>
                <Link to="/legal/founder-terms" className="flex-1 rounded-full border border-white/15 px-5 py-2.5 text-sm text-white">Read Program Terms</Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
